/**
 * Age determination for COPPA.
 *
 * Audit finding C3, break #1: the platform collected no age at all, so it could
 * not identify which accounts were subject to COPPA. `isUnder13` existed inside
 * `parentalConsentService` but was only reachable from a self-declared age typed
 * into a page nothing linked to.
 *
 * This module is the single source of truth for the client. The Cloud Functions
 * package cannot import from `src/`, so `functions/src/consentFunctions.ts`
 * carries a deliberate copy — if you change the threshold or the birthday
 * arithmetic here, change it there too.
 */

/**
 * COPPA applies to children under 13. 16 CFR § 312.2 defines "child" as an
 * individual under the age of 13.
 */
export const COPPA_AGE_THRESHOLD = 13;

/** Earliest birth date we will accept, as a sanity bound on typos. */
const MAX_AGE_YEARS = 120;

/** A birth date as calendar components — deliberately not an instant. */
interface CalendarDate {
  year: number;
  month: number; // 1-12
  day: number;   // 1-31
}

/**
 * Normalise input to calendar components.
 *
 * This exists because of a real bug: `new Date('2000-09-06')` is parsed as
 * midnight **UTC**, but `getFullYear()`/`getMonth()`/`getDate()` read **local**
 * time. West of Greenwich that combination reports the previous day, which
 * shifts a computed age by one day — precisely at the 13th-birthday boundary
 * that decides whether COPPA applies.
 *
 * A date of birth is a calendar date, not a moment, so `YYYY-MM-DD` strings
 * (what `<input type="date">` produces) are split on their components and
 * never round-tripped through UTC parsing.
 */
const toCalendarDate = (value: Date | string): CalendarDate | null => {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return { year, month, day };
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return { year: parsed.getFullYear(), month: parsed.getMonth() + 1, day: parsed.getDate() };
  }

  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  // A Date built from a date-only string holds UTC midnight; read it back in
  // UTC so the calendar day survives the round trip.
  return {
    year: value.getUTCFullYear(),
    month: value.getUTCMonth() + 1,
    day: value.getUTCDate(),
  };
};

/**
 * Whole years elapsed between `birthDate` and `now`.
 *
 * Counts a birthday as reached only on or after the day itself, so someone
 * whose 13th birthday is tomorrow is still 12 today. Returns null for input
 * that is not a usable date, or for a date in the future.
 */
export const ageInYears = (birthDate: Date | string, now: Date = new Date()): number | null => {
  const dob = toCalendarDate(birthDate);
  if (!dob) return null;

  // "Today" is read in the viewer's LOCAL calendar, because "are you 13 yet"
  // is a question about their day, not UTC's. Reading `now` in UTC would put
  // an evening user east of Greenwich onto tomorrow's date and could credit a
  // 13th birthday a day early — the one direction that fails open.
  const today: CalendarDate = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };

  let age = today.year - dob.year;
  if (today.month < dob.month || (today.month === dob.month && today.day < dob.day)) {
    age -= 1;
  }

  if (age < 0) return null; // born in the future
  return age;
};

/**
 * True when the birth date belongs to a child COPPA covers.
 *
 * Fails CLOSED: unparseable or absent input returns true, so a malformed date
 * results in the protective path (consent required) rather than silently
 * treating an unknown user as an adult.
 */
export const isUnder13 = (birthDate: Date | string | null | undefined, now: Date = new Date()): boolean => {
  if (birthDate === null || birthDate === undefined || birthDate === '') return true;
  const age = ageInYears(birthDate, now);
  if (age === null) return true;
  return age < COPPA_AGE_THRESHOLD;
};

/**
 * The `requiresParentalConsent` value to RECORD for a date of birth (S230).
 *
 * Covered if the account was under 13 at any moment in the last day. The rules accept "not
 * covered" only once the 13th birthday is at least a day past (`ageFieldsConsistent` in
 * firestore.rules), which absorbs time-zone differences — so the browser applies the same margin,
 * or a child registering on their 13th birthday would be refused.
 */
export const requiresConsentFlag = (
  birthDate: Date | string | null | undefined,
  now: Date = new Date()
): boolean => isUnder13(birthDate, now) || isUnder13(birthDate, new Date(now.getTime() - 24 * 60 * 60 * 1000));

/**
 * Validate a date-of-birth string from the registration form.
 * Returns null when valid, or a message to show the user.
 */
export const validateBirthDate = (value: string, now: Date = new Date()): string | null => {
  if (!value) return 'Please enter your date of birth';

  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) return 'Please enter a valid date';
  if (dob.getTime() > now.getTime()) return 'Date of birth cannot be in the future';

  const age = ageInYears(dob, now);
  if (age === null) return 'Please enter a valid date';
  if (age > MAX_AGE_YEARS) return 'Please check the year you entered';

  return null;
};
